/**
 * Admin Orcaloops Outcome RPC definitions (OL-81, OL-88).
 *
 * The OPERATOR surface for the outcome engine: it sits behind
 * `AdminMiddleware` and takes an EXPLICIT `customerId`, because an operator
 * acts across customers. The member-facing, actor-scoped `outcomes.*` group
 * arrives later and derives its customer from `CurrentActor` membership — it
 * must never accept a payload `customerId`.
 *
 * This group is pre-created by the seam issue with a single `list` rpc so the
 * wiring (registration in `server.ts`, the handlers layer, the re-exports) is
 * already in place. The outcome-admin issue ADDS create/get/cancel/resume rpcs
 * to this same group; it does not create a new one.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Outcome } from "@fm/db/models/Outcome";
import { Report } from "@fm/db/models/Report";
import { Run } from "@fm/db/models/Run";
import { Trigger, TriggerKind } from "@fm/db/models/Trigger";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";

/**
 * The customer whose outcomes are being listed. Explicit, not derived — see the
 * operator-surface note above.
 *
 * @since 1.0.0
 */
export const OutcomeListInput = Schema.Struct({
  customerId: Schema.String,
});
export type OutcomeListInput = Schema.Schema.Type<typeof OutcomeListInput>;

/**
 * The standing trigger an operator arms alongside a new outcome (OL-67) —
 * flattened, so the wire payload carries no nested `spec` object the caller has
 * to know the shape of.
 *
 * Per kind: `cron` + optional `tz` for a schedule, `repo` + optional `branch`
 * for `merge` / `push`, nothing at all for `manual`. The handler validates the
 * combination BEFORE anything is written — an unparsable cron or a repo the
 * customer does not own is a `ValidationError` with no outcome row created.
 *
 * @since 1.0.0
 */
export const TriggerSpecInput = Schema.Struct({
  kind: TriggerKind,
  repo: Schema.optional(Schema.String),
  branch: Schema.optional(Schema.String),
  cron: Schema.optional(Schema.String),
  tz: Schema.optional(Schema.String),
});
export type TriggerSpecInput = Schema.Schema.Type<typeof TriggerSpecInput>;

/**
 * A new outcome, as an operator files it.
 *
 * `budgetUsd` is **required and non-null here** even though the column is
 * nullable: an outcome with no budget never stops (the workflow only halts once
 * `spentUsd >= budgetUsd`), and this is the surface that spawns paid agent
 * containers. `acceptance` stays nullable — the statement often carries the
 * acceptance criteria itself.
 *
 * `maxParallel` / `maxTurns` are optional; the handler applies the column
 * defaults (3 / 20) when they are absent.
 *
 * @since 1.0.0
 */
export const OutcomeCreateInput = Schema.Struct({
  customerId: Schema.String,
  title: Schema.String,
  statement: Schema.String,
  acceptance: Schema.NullOr(Schema.String),
  repo: Schema.String,
  ref: Schema.String,
  budgetUsd: Schema.Number,
  maxParallel: Schema.optional(Schema.Int),
  maxTurns: Schema.optional(Schema.Int),
  /** Arms the outcome to STAND (OL-67). Absent = a one-shot outcome. */
  trigger: Schema.optional(TriggerSpecInput),
});
export type OutcomeCreateInput = Schema.Schema.Type<typeof OutcomeCreateInput>;

/**
 * The created outcome plus the `attempt` discriminator its first dispatch used.
 *
 * `attempt` is returned rather than persisted — `orcaloops.outcomes` has no
 * `workflow_attempt` column. The durable handle is `workflowExecutionId` on the
 * row, which is what cancel/resume address; a later wave that re-arms an
 * outcome mints a fresh attempt of its own.
 *
 * @since 1.0.0
 */
export const OutcomeCreateResponse = Schema.Struct({
  outcome: Outcome.json,
  attempt: Schema.String,
  /**
   * The trigger that was armed, when the payload asked for one (OL-67).
   * OPTIONAL rather than nullable so `{ outcome, attempt }` stays exactly what
   * it was — a caller that does not arm a trigger sees the wave-1 shape byte
   * for byte.
   */
  trigger: Schema.optional(Trigger.json),
});
export type OutcomeCreateResponse = Schema.Schema.Type<typeof OutcomeCreateResponse>;

/**
 * One outcome addressed by id, scoped to its owner. Shared by `get`, `cancel`
 * and `resume` — all three address exactly this coordinate.
 *
 * @since 1.0.0
 */
export const OutcomeRefInput = Schema.Struct({
  customerId: Schema.String,
  outcomeId: Schema.String,
});
export type OutcomeRefInput = Schema.Schema.Type<typeof OutcomeRefInput>;

/**
 * One run in an outcome's tree, as the detail view renders it.
 *
 * Derived from `Run.json` by struct-omit rather than redeclared, so a new column
 * reaches the client without a second edit — but the wire row must NOT be
 * `Run.json` itself, because that carries two secrets: `deferredToken` (the
 * plaintext `@effect/workflow` DurableDeferred token, which
 * `packages/db/src/tables/runs.ts` declares "never leaves the api" — holding it
 * lets you resolve the workflow directly) and `callbackTokenHash` (the run's
 * capability digest). Neither is needed to render a run tree. Same treatment,
 * same reasoning as `OutcomeInboxRow` in `orcaloops-human-waits.rpc.ts`.
 *
 * @since 1.0.0
 */
export const OutcomeRunRow = Run.json.pipe(Schema.omit("deferredToken", "callbackTokenHash"));
export type OutcomeRunRow = Schema.Schema.Type<typeof OutcomeRunRow>;

/**
 * An outcome with its run tree and every report those runs produced — the
 * operator's single-outcome view.
 *
 * @since 1.0.0
 */
export const OutcomeDetailResponse = Schema.Struct({
  outcome: Outcome.json,
  runs: Schema.Array(OutcomeRunRow),
  reports: Schema.Array(Report.json),
});
export type OutcomeDetailResponse = Schema.Schema.Type<typeof OutcomeDetailResponse>;

/**
 * What one `admin.outcomes.fire` actually did, reported honestly (OL-67).
 *
 * `fired` means a new execution was dispatched and `attempt` is its
 * discriminator. `coalesced` means the outcome was already in flight, so the
 * fire was queued instead — `attempt` is the parked id when this call parked it,
 * and `null` when a fire was already waiting (the queue is exactly one deep).
 * Both `false` means the outcome was cancelled or budget-exhausted: nothing was
 * dispatched, nothing was queued.
 *
 * @since 1.0.0
 */
export const OutcomeFireResponse = Schema.Struct({
  fired: Schema.Boolean,
  coalesced: Schema.Boolean,
  attempt: Schema.NullOr(Schema.String),
});
export type OutcomeFireResponse = Schema.Schema.Type<typeof OutcomeFireResponse>;

/**
 * Admin Orcaloops Outcome RPC group.
 *
 * The wire row is `Outcome.json` — the model IS the wire type and is never
 * redeclared here, so a new column reaches the client without a second edit
 * (timestamps ride through as `Schema.DateTimeUtc`). Unlike `Run`, `Outcome`
 * carries no secret, so nothing is omitted.
 *
 * `Forbidden` appears on every rpc because the middleware itself raises it; on
 * `create` it doubles as the `ORCALOOPS_OUTCOMES_DISABLED` kill-switch refusal.
 *
 * @since 1.0.0
 */
export class AdminOrcaloopsOutcomeRpcs extends RpcGroup.make(
  Rpc.make("admin.outcomes.list", {
    payload: OutcomeListInput,
    success: Schema.Array(Outcome.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.create", {
    payload: OutcomeCreateInput,
    success: OutcomeCreateResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  // `get` — deliberately named for the sibling admin orca surface
  // (`admin.orcaProject.get`, `admin.orcaRepo.get`). The repo's
  // "`find*` never `get*`" rule governs DB accessor methods, and every accessor
  // behind this handler is a `find*`.
  Rpc.make("admin.outcomes.get", {
    payload: OutcomeRefInput,
    success: OutcomeDetailResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.cancel", {
    payload: OutcomeRefInput,
    success: Outcome.json,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      InvalidStateTransition,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.resume", {
    payload: OutcomeRefInput,
    success: Outcome.json,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      InvalidStateTransition,
    ),
  }).middleware(AdminMiddleware),
  // --- OL-67 (standing triggers). Three ADDED rpcs; the five above are
  // untouched.
  //
  // `pauseTriggers` / `resumeTriggers` are deliberately NOT named `pause` /
  // `resume`: `admin.outcomes.resume` above already exists and resumes the
  // outcome's WORKFLOW EXECUTION, and a second rpc of that exact name
  // in this group would be a duplicate-name collision. These two only flip
  // `triggers.enabled`; they never touch the outcome's status or its execution.
  Rpc.make("admin.outcomes.pauseTriggers", {
    payload: OutcomeRefInput,
    success: Schema.Array(Trigger.json),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.resumeTriggers", {
    payload: OutcomeRefInput,
    success: Schema.Array(Trigger.json),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.fire", {
    payload: OutcomeRefInput,
    success: OutcomeFireResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      InvalidStateTransition,
    ),
  }).middleware(AdminMiddleware),
) {}
